import { Component, Input, OnInit } from '@angular/core';
import { ContentDataByDataviewDTO, Table } from '@tj/tj-dmp-lib-api';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

interface ProductTypeConfig {
  type: string;
  table: string;
  label: string;
  count: number;
}

@Component({
    selector: 'dmp-dyn-raw-detail-purchases',
    templateUrl: './dyn-raw-detail-purchases.component.html',
    styleUrls: ['./dyn-raw-detail-purchases.component.scss'],
    standalone: false
})
export class DynRawDetailPurchasesComponent implements OnInit {

  @Input() data: ContentDataByDataviewDTO;
  @Input() tables: Table[];
  @Input() tenant: string;
  @Input() partner: string;
  @Input() personalId: number;

  // Variables pour les commandes
  lastestThreeCommandsIndex: { index: number }[] = [];
  products: ProductTypeConfig[] = [
    { type: "flight", table: "flight", label: "view.details.purchases.flights", count: 0 },
    { type: "hotel", table: "hotel", label: "view.details.purchases.hotels", count: 0 },
    { type: "car", table: "carrental", label: "view.details.purchases.carrentals", count: 0 },
    { type: "parking", table:" parking", label: "view.details.purchases.parkings", count: 0 }
  ];
  commandFields: string[] = ['booking_reference', 'booking_date', 'product_type', 'sales_gross'];
  loyaltyMap: { [key: string]: string };

  // Variables pour les statistiques de voyages
  travelStats: Map<string, any> = new Map<string, any>();

  constructor(
    private messageService: MessageService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    const commands = this.data?.dataSubQuery?.command?.content || [];
    let commandDates: Map<number, string> = new Map<number, string>();

    if (commands.length > 0) {
      this.countProductTypes(commands);
      this.findLatestThreeCommands(commands, commandDates);
      this.extractTravelStatisticsFromCommands(commands);
    }

    // Extract loyalty data from the existing data (already loaded by parent)
    const affiliateKey = `personal_affilie_${this.partner.toLowerCase()}`;
    this.loyaltyMap = {
      loyalty_status: this.data.mainData.rows[`${affiliateKey}.loyalty_status`],
      loyalty_status_date: this.data.mainData.rows[`${affiliateKey}.loyalty_status_date`]
    };
  }

  private countProductTypes(commands: any[]): void {
    commands.forEach(command => {
      let productType: string = command['rows']['command.product_type']?.toLowerCase() || '';
      for(const item of this.products){
        const type = item.type;
        if(productType.includes(type)){
          item.count += 1;
          break;

        }
      }
    });
  }

  private findLatestThreeCommands(commands: any[], commandDates: Map<number, string>): void {
    commands.forEach(command => {
      commandDates.set(command['id'], command['rows']['command.booking_date']);
    });

    const commandsArray = Array.from(commandDates.entries());
    const sortedCommands = commandsArray.sort((a, b) => {
      const dateA = new Date(a[1]).getTime();
      const dateB = new Date(b[1]).getTime();
      return dateB - dateA;
    });

    const latestThreeCommandsArray = sortedCommands.slice(0, 3);
    this.lastestThreeCommandsIndex = [];

    latestThreeCommandsArray.forEach(item => {
      const index = commands.findIndex(command => command['id'] == item[0]);
      if (index !== -1) {
        this.lastestThreeCommandsIndex.push({ index });
      }
    });
  }


  /**
   * Extract travel statistics directly from command data (no additional API call needed)
   */
  private extractTravelStatisticsFromCommands(commands: any[]): void {
    const originAirports = new Map<string, number>();
    const destinationAirports = new Map<string, number>();
    const hotelCities = new Map<string, number>();
    const carPickupLocations = new Map<string, number>();
    const parkingLocations = new Map<string, number>();

    commands.forEach(command => {
      const rows: any = command.rows;

      // Extract flight data from command rows
      if (rows['command.outbound_origin_airport'] != null) {
        const airport = rows['command.outbound_origin_airport'];
        originAirports.set(airport, (originAirports.get(airport) || 0) + 1);
      }
      if (rows['command.outbound_destination_airport'] != null) {
        const airport = rows['command.outbound_destination_airport'];
        destinationAirports.set(airport, (destinationAirports.get(airport) || 0) + 1);
      }

      // Extract hotel data from command rows
      if (rows['command.accom_city_name'] != null) {
        const city = rows['command.accom_city_name'];
        hotelCities.set(city, (hotelCities.get(city) || 0) + 1);
      }

      // Extract car rental data from command rows
      if (rows['command.pick-up_location'] != null) {
        const location = rows['command.pick-up_location'];
        carPickupLocations.set(location, (carPickupLocations.get(location) || 0) + 1);
      }

      // Extract parking data from command rows
      if (rows['command.airport'] != null) {
        const airport = rows['command.airport'];
        parkingLocations.set(airport, (parkingLocations.get(airport) || 0) + 1);
      }
    });

    // Store statistics
    if (originAirports.size > 0) {
      this.travelStats.set('flightOriginAirports', originAirports);
    }
    if (destinationAirports.size > 0) {
      this.travelStats.set('flightDestinationAirports', destinationAirports);
    }
    if (hotelCities.size > 0) {
      this.travelStats.set('hotelCities', hotelCities);
    }
    if (carPickupLocations.size > 0) {
      this.travelStats.set('carPickupLocations', carPickupLocations);
    }
    if (parkingLocations.size > 0) {
      this.travelStats.set('parkingLocations', parkingLocations);
    }
  }

  getCommandsCount(): number {
    return this.data?.dataSubQuery?.command?.size || 0;
  }
  
  hasStats(type: string): boolean {
    const stats = this.travelStats.get(type);
    return stats && stats.size > 0;
  }

  openCustomerPurchases() {
    const url = this.buildUrl('command', {key:"email", value:this.data.mainData.rows['personal.email']}, 'command');
    window.open(url, '_blank');
  }

  openPurchaseDetails(index: number){
    const bookingRef: string = this.data?.dataSubQuery?.command?.content[index]['rows']['command.booking_reference'];
    const productType: string = this.data?.dataSubQuery?.command?.content[index]['rows']['command.product_type']?.toLowerCase() || '';
    let viewCode:string = "command"
    const productTypes = this.products.map(item => item.type)
    for (const type of productTypes) {
      if (productType.includes(type)) {
        if(type === 'car'){
          viewCode += '-carrental';
        }
        else {
          viewCode += "-" + type;
        }
        break;
      }
    }
    const url = this.buildUrl('command', {key:"ref", value:bookingRef}, viewCode, true);
    window.open(url, '_blank');

  }
  
  private buildUrl(groupDataview: string, param:{key:string, value:string}, viewCode:string, detail:boolean = false): string{    
    return `/h/view/${groupDataview}?${param.key}=${encodeURIComponent(param.value)}&tenant=${this.tenant}&partner=${this.partner}&code=${viewCode}&detail=${detail}`;
  }

  getSubscriptionDate(): Date | null {
    if (this.loyaltyMap && this.loyaltyMap.loyalty_status_date) {
      const date = this.loyaltyMap.loyalty_status_date
      return date ? new Date(date) : null;
    }
    return null;
  }


  isLoyal(): boolean {
    if (this.loyaltyMap && this.loyaltyMap.loyalty_status) {
      return this.loyaltyMap.loyalty_status === 'LOYAL'
    }
    return false
  }

  
}
